import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation
} from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  ValidatorFn,
  AbstractControl,
  ValidationErrors
} from '@angular/forms';
import { State } from 'src/app/core/models/enums/State';
import Utils from 'src/app/shared/utilities/utils';
import { ICreditCard } from '../../../core/interfaces/payments/ICreditCard';
import { MatStepper } from '@angular/material/stepper';
import { AutoPopulateService } from 'src/app/core/services/auto-populate.service';
import { StateMap } from 'src/app/core/models/constants/app-constants';

@Component({
    selector: 'app-credit-card-payment',
    templateUrl: './credit-card-payment.component.html',
    styleUrls: ['./credit-card-payment.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class CreditCardPaymentComponent implements OnInit {
  @Input() amountDue: number = 0;
  @Output() submitPayment = new EventEmitter<ICreditCard>();
  public creditCardForm: FormGroup;
  public billingAddressForm: FormGroup;
  public states: string[] = this.getStates();

  public years: number[] = [];
  public months: string[] = [
    '01 - January',
    '02 - February',
    '03 - March',
    '04 - April',
    '05 - May',
    '06 - June',
    '07 - July',
    '08 - August',
    '09 - September',
    '10 - October',
    '11 - November',
    '12 - December'
  ];

  //#region Initialization

  constructor(
    private formBuilder: FormBuilder,
    private autoPopulateService: AutoPopulateService
  ) {}

  ngOnInit(): void {
    let year: number = new Date().getFullYear();
    const limit: number = year + 20;
    for (; year < limit; year++) {
      this.years.push(year);
    }

    this.creditCardForm = this.formBuilder.group(
      {
        amount: [
          this.amountDue,
          Validators.compose([
            Validators.required,
            Validators.min(1),
            Validators.max(this.amountDue)
          ])
        ],
        name: ['', Validators.required],
        cardNumber: ['', Validators.required],
        expMonth: ['', Validators.required],
        expYear: ['', Validators.required],
        cvvCode: ['', Validators.required]
      },
      {
        validators: [expiryValidator()]
      }
    );

    this.billingAddressForm = this.formBuilder.group({
      street: ['', Validators.required],
      line2: [''],
      zip: [
        '',
        [
          Validators.required,
          Validators.pattern('^[0-9]{5}$|^[0-9]{5}-[0-9]{4}$')
        ]
      ],
      city: ['', Validators.required],
      state: ['', Validators.required]
    });
  }

  //#endregion Initialization

  //#region Action handlers

  public onSubmitPayment(): void {
    const amount: number = parseFloat(
      this.creditCardForm.controls['amount'].value
    );
    const name: string = this.creditCardForm.controls['name'].value;
    const cardNumber: string = this.creditCardForm.controls['cardNumber'].value;
    const expMonth: number = parseInt(
      this.creditCardForm.controls['expMonth'].value
    );
    const expYear: number = parseInt(
      this.creditCardForm.controls['expYear'].value
    );
    const code: string = this.creditCardForm.controls['cvvCode'].value;
    const line1: string = this.billingAddressForm.controls['street'].value;
    const line2: string = this.billingAddressForm.controls['line2'].value;
    const city: string = this.billingAddressForm.controls['city'].value;
    const state: State = this.billingAddressForm.controls['state'].value;
    const zip: string = this.billingAddressForm.controls['zip'].value;

    const payment: ICreditCard = {
      amount: amount,
      cardHolderName: name,
      cardNumber: cardNumber,
      expirationMonth: expMonth,
      expirationYear: expYear,
      securityCode: code,
      isSwiped: false,
      billingAddress: {
        line1: line1,
        line2: line2,
        city: city,
        state: state,
        zipCode: zip
      }
    };

    this.submitPayment.emit(payment);
  }

  public onZipChange(): void {
    const zipCode = this.billingAddressForm.controls.zip.value;
    if (zipCode.length < 5) {
      return;
    }

    this.autoPopulateService
      .getPreferredCityStateFromZip(zipCode)
      .subscribe((response) => {
        if (response.hasData()) {
          const city = Utils.toFullProper(response.data[0].city);
          const state = Utils.removeWhiteSpace(
            StateMap.get(response.data[0].state)
          );

          this.billingAddressForm.patchValue({ city: city });
          this.billingAddressForm.patchValue({ state: state });
        }
      });
  }

  //#endregion Action handlers

  //#region Helpers

  public getStates(): string[] {
    return Object.keys(State).filter((key) => {
      return !Utils.isNumber(key) && key !== 'None';
    });
  }
  //#endregion Helpers
}

export function expiryValidator(): ValidatorFn {
  return (form: FormGroup): ValidationErrors | null => {
    let expiredCard = false;

    const expMonth = parseInt(form.get('expMonth').value);
    const expYear = parseInt(form.get('expYear').value);

    if (expMonth && expYear) {
      const now = new Date();
      if (expYear < now.getFullYear()) {
        expiredCard = true;
      }

      if (expYear == now.getFullYear() && expMonth < now.getMonth()) {
        expiredCard = true;
      }
    }

    return expiredCard ? { expired: true } : null;
  };
}
